import { ConsultationType } from '../@types'
import { Product } from '../data/use-products/types'
import { CartPrice, DiscountPrice } from '../hooks/use-cart-price'
import { isPercentageDiscount } from '../lib/discounts'

import {
  GoogleTagManager,
  GTMEventAddPaymentInfo,
  GTMEventBeginCheckout,
  GTMEventPageView,
  GTMEventPurchase,
  GTMEventSelectItem,
  GTMEventSignUp,
  GTMEventTrackConfirmAge,
  GTMEventTrackPayMethod,
  GTMEventTrackScheduleItem,
  GTMEventTrackSign,
  GTMEventTrackWarning,
  GTMEventTutorialBegin,
  GTMEventTutorialComplete,
  ReferralSource,
} from './gtm/GoogleTagManager'

export enum PaymentTypes {
  CREDIT_CARD_NEW = 'Credit Card New',
  CREDIT_CARD_SAVED = 'Credit Card Saved',
}

export const TRACKING_CURRENCY = 'EUR'

const getDiscountValues = (cartPrice: CartPrice) => {
  if (isPercentageDiscount(cartPrice)) {
    const { amount, discount, originalAmount } = cartPrice as DiscountPrice

    return {
      finalPrice: amount,
      coupon: discount.name,
      discount: originalAmount - amount, // Discounted amount in currency units
    }
  }

  return {
    finalPrice: cartPrice?.amount,
    coupon: '',
    discount: 0,
  }
}

export const trackScheduleItem = (referralSource: ReferralSource = ReferralSource.NONE) => {
  try {
    const event = GTMEventTrackScheduleItem.new(referralSource)
    GoogleTagManager.pushEvent(event)
  } catch (err) {
    console.error('failed to track event schedule item:', err)
  }
}

export const trackPayMethod = () => {
  try {
    GoogleTagManager.pushEvent(GTMEventTrackPayMethod.new())
  } catch (err) {
    console.error('failed to track event pay method:', err)
  }
}

export const trackPageView = () => {
  try {
    GoogleTagManager.pushEvent(GTMEventPageView.new())
  } catch (err) {
    console.error('failed to track event:', err)
  }
}

export const trackTutorialBegin = () => {
  try {
    GoogleTagManager.pushEvent(GTMEventTutorialBegin.new())
  } catch (err) {
    console.error('failed to track event tutorial begin:', err)
  }
}

export const trackTutorialComplete = () => {
  try {
    GoogleTagManager.pushEvent(GTMEventTutorialComplete.new())
  } catch (err) {
    console.error('failed to track event tutorial complete:', err)
  }
}

export const trackSign = () => {
  try {
    GoogleTagManager.pushEvent(GTMEventTrackSign.new())
  } catch (err) {
    console.error('failed to track event sign up:', err)
  }
}

export const trackConfirmAge = () => {
  try {
    GoogleTagManager.pushEvent(GTMEventTrackConfirmAge.new())
  } catch (err) {
    console.error('failed to track event confirm age:', err)
  }
}

export const trackWarning = () => {
  try {
    GoogleTagManager.pushEvent(GTMEventTrackWarning.new())
  } catch (err) {
    console.error('failed to track event warning:', err)
  }
}

export const trackSelectItem = (
  product: Product,
  consultationType: ConsultationType,
  referralSource: ReferralSource = ReferralSource.NONE
) => {
  try {
    const event = GTMEventSelectItem.new(product, consultationType, referralSource)
    GoogleTagManager.pushEvent(event)
  } catch (err) {
    console.error('failed to track event item selection:', err)
  }
}

export const trackSignUp = () => {
  try {
    GoogleTagManager.pushEvent(GTMEventSignUp.new())
  } catch (err) {
    console.error('failed to track event sign up', err)
  }
}

export const trackPurchase = (product: Product, cartPrice: CartPrice, orderUuid: string) => {
  const { coupon, discount } = getDiscountValues(cartPrice)

  try {
    GoogleTagManager.pushEvent(GTMEventPurchase.new(orderUuid, product, ConsultationType.ON_DEMAND, discount, coupon))
  } catch (err) {
    console.error(`Failed to track event purchase`, err)
  }
}

export const trackAddPaymentInfo = () => {
  try {
    GoogleTagManager.pushEvent(GTMEventAddPaymentInfo.new())
  } catch (err) {
    console.error(`Failed to track event add_payment_info`, err)
  }
}

export const trackCheckout = () => {
  try {
    GoogleTagManager.pushEvent(GTMEventBeginCheckout.new())
  } catch (err) {
    console.error(`Failed to track event begin_checkout`, err)
  }
}
